import {
  addDays,
  addWeeks,
  format,
  getDay,
  subDays,
  isSaturday,
  isSunday,
  lastDayOfMonth,
  parseISO,
  lastDayOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { WorkloadType } from '@components/workload/switch/types';
import { DisplayOptionSetting } from '@services/resources/users/displayOptions/types';
import { WorkloadCalendarSchedule } from '@services/workload/calendar/types';
import { WorkloadMonthlyColumn, WorkloadWeekColumn } from './types';
import { DAY_KEY_FORMAT } from './constants';

export const isHiddenWeekendDay = (day: Date, settings: DisplayOptionSetting = {}) =>
  (!settings.weekendsSaturday && isSaturday(day)) || (!settings.weekendsSunday && isSunday(day));

export const getDayKey = (date: Date) => format(date, DAY_KEY_FORMAT);

export const generateWeekColumns = (
  visibleDays: number,
  date: Date,
  settings: DisplayOptionSetting | undefined,
  expandedDays: string[],
): WorkloadWeekColumn[] => {
  const result: WorkloadWeekColumn[] = [];
  let fullDays = 0;
  let day = date;

  while (fullDays < visibleDays || isHiddenWeekendDay(day, settings)) {
    const hidden = isHiddenWeekendDay(day, settings);
    const expanded = hidden && expandedDays.includes(getDayKey(day));
    const collapsed = hidden && !expanded;

    result.push({
      date: day,
      collapsed,
      expanded,
      dayIndex: collapsed ? -1 : result.filter((column) => !column.collapsed).length,
      hiddenCount: 0,
    });
    if (!collapsed) fullDays++;
    day = addDays(day, 1);
  }

  return result;
};

export const countSchedulesByDay = (
  schedules: Pick<WorkloadCalendarSchedule, 'id' | 'startDate'>[],
): Record<string, number> => {
  const idsByDay = new Map<string, Set<string>>();

  schedules.forEach(({ id, startDate }) => {
    if (!startDate) return;
    const key = getDayKey(parseISO(startDate));
    if (!idsByDay.has(key)) idsByDay.set(key, new Set());
    idsByDay.get(key)?.add(id);
  });

  return Object.fromEntries(Array.from(idsByDay, ([key, ids]) => [key, ids.size]));
};

export const toggleExpandedDay = (days: string[], date: Date): string[] => {
  const key = getDayKey(date);
  return days.includes(key) ? days.filter((day) => day !== key) : [...days, key];
};

const MONTHLY_WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

export const weekdayHasSchedules = (
  days: Date[],
  weekday: number,
  countsByDay: Record<string, number>,
) => days.some((day) => getDay(day) === weekday && Boolean(countsByDay[getDayKey(day)]));

export const generateMonthlyColumns = (
  days: Date[],
  settings: DisplayOptionSetting,
  expandedWeekdays: number[],
  countsByDay: Record<string, number>,
): WorkloadMonthlyColumn[] =>
  MONTHLY_WEEKDAYS.flatMap<WorkloadMonthlyColumn>((weekday) => {
    const hidden =
      (weekday === 6 && !settings.weekendsSaturday) || (weekday === 0 && !settings.weekendsSunday);
    if (!hidden) return [{ weekday, collapsed: false, expanded: false }];
    if (expandedWeekdays.includes(weekday)) return [{ weekday, collapsed: false, expanded: true }];

    return weekdayHasSchedules(days, weekday, countsByDay)
      ? [{ weekday, collapsed: true, expanded: false }]
      : [];
  });

export const toggleExpandedWeekday = (weekdays: number[], weekday: number): number[] =>
  weekdays.includes(weekday) ? weekdays.filter((day) => day !== weekday) : [...weekdays, weekday];

export const getAdjacentVisibleDay = (
  date: Date,
  direction: 'prev' | 'next',
  settings?: DisplayOptionSetting,
): Date => {
  const step = direction === 'next' ? addDays : subDays;
  let day = step(date, 1);

  while (isHiddenWeekendDay(day, settings)) day = step(day, 1);

  return day;
};

export const generateDays = (
  visibleDays: number,
  date: Date,
  settings?: DisplayOptionSetting,
): Date[] => {
  let days = 0;
  const result = [];
  const limit = visibleDays;

  while (days < limit) {
    const newDay = addDays(date, days);
    if (
      (settings && !settings?.weekendsSaturday && isSaturday(newDay)) ||
      (settings && !settings?.weekendsSunday && isSunday(newDay))
    ) {
      days++;
      continue;
    } else {
      result.push(newDay);
      days++;
    }
  }

  return result;
};

export const getFirstDay = (date: Date, type?: WorkloadType) => {
  const first = ['two-week', 'week', 'agenda'].includes(type || '') ? date : startOfMonth(date);
  return startOfWeek(first, { weekStartsOn: 1 });
};

export const getLastDay = (date: Date, type?: WorkloadType) => {
  const last = ['two-week', 'agenda'].includes(type || '')
    ? addWeeks(date, 1)
    : lastDayOfMonth(date);
  return lastDayOfWeek(last, { weekStartsOn: 1 });
};

export const getSidebarTab = (settings?: DisplayOptionSetting) => {
  if (!settings) return 'jobs';
  if (settings?.schedulingSettingsHideSchedulerSidebar) return undefined;
  return 'jobs';
};
